import { useMemo, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft,
  Search,
  X,
  Building2,
  GitBranch,
  Tag,
  Database,
  History,
} from "lucide-react";

import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { cn } from "@/lib/utils";
import {
  platformEntries,
  topLevelPlatform,
  findPlatformEntry,
} from "./platformSearchIndex";
import type { SettingsEntry } from "./settingsSearchIndex";

export function PlatformSidebar() {
  const { t } = useTranslation("settings");
  const navigate = useNavigate();
  const { state } = useSidebar();
  const collapsed = state === "collapsed";
  const { pathname } = useLocation();
  const [query, setQuery] = useState("");

  const isActive = (path: string) =>
    pathname === path || (path !== "/platform" && pathname.startsWith(path + "/"));

  const trimmedQuery = query.trim().toLowerCase();
  const searching = trimmedQuery.length > 0;

  const results = useMemo(() => {
    if (!searching) return [];
    const words = trimmedQuery.split(/\s+/).filter(Boolean);
    return platformEntries.filter((entry) => {
      const parent = entry.parentId ? findPlatformEntry(entry.parentId) : undefined;
      const haystack = [
        t(entry.titleKey),
        parent ? t(parent.titleKey) : "",
        ...entry.keywords,
      ]
        .join(" ")
        .toLowerCase();
      return words.every((w) => haystack.includes(w));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searching, trimmedQuery, t]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      setQuery("");
    } else if (e.key === "Enter" && results.length > 0) {
      navigate(results[0].path);
      setQuery("");
    }
  };

  const renderMenuButton = (entry: SettingsEntry, withParent = false) => {
    const Icon = entry.icon;
    const parent = withParent && entry.parentId ? findPlatformEntry(entry.parentId) : undefined;
    return (
      <SidebarMenuItem key={entry.id}>
        <SidebarMenuButton
          asChild
          isActive={!withParent && isActive(entry.path)}
          tooltip={t(entry.titleKey)}
          className={parent ? "h-auto py-1.5 items-start" : undefined}
        >
          <NavLink to={entry.path} onClick={() => setQuery("")}>
            <Icon className={cn("h-4 w-4 shrink-0", parent && "mt-0.5")} />
            {!collapsed && (
              <span className="flex min-w-0 flex-col">
                <span className="truncate">{t(entry.titleKey)}</span>
                {parent && (
                  <span className="truncate text-xs text-muted-foreground">
                    {t(parent.titleKey)}
                  </span>
                )}
              </span>
            )}
          </NavLink>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  };

  const menuItems = topLevelPlatform.filter((e) => e.id !== "pNavigation");
  const platformGroup = menuItems.filter((e) =>
    ["pCompanies", "pWorkflows", "pBrands", "pData"].includes(e.id),
  );
  const administrationGroup = menuItems.filter((e) =>
    ["pUsers", "pAuditLog"].includes(e.id),
  );


  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="p-2">
        <button
          type="button"
          onClick={() => navigate("/")}
          className={cn(
            "flex items-center gap-2 text-sm text-sidebar-foreground hover:text-sidebar-foreground/80 transition-colors",
            collapsed ? "justify-center" : "px-2",
          )}
          aria-label={t("backToApp")}
        >
          <ArrowLeft className="h-4 w-4 shrink-0" />
          {!collapsed && <span className="truncate">{t("backToApp")}</span>}
        </button>

        {!collapsed && (
          <div className="relative mt-2">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t("search.placeholder")}
              aria-label={t("search.placeholder")}
              className="w-full rounded-md border border-border bg-card py-1.5 pl-8 pr-8 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
            {searching && (
              <button
                type="button"
                onClick={() => setQuery("")}
                aria-label={t("search.clear")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        )}
      </SidebarHeader>

      <SidebarContent>
        {searching ? (
          <SidebarGroup>
            <SidebarGroupContent>
              {results.length > 0 ? (
                <SidebarMenu>
                  {results.map((entry) => renderMenuButton(entry, Boolean(entry.parentId)))}
                </SidebarMenu>
              ) : (
                <p className="px-2 py-3 text-sm text-muted-foreground">{t("search.noResults")}</p>
              )}
            </SidebarGroupContent>
          </SidebarGroup>
        ) : (
          <>
            <SidebarGroup>
              <SidebarGroupLabel>{t("sections.platform")}</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>{platformGroup.map((e) => renderMenuButton(e))}</SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>

            <SidebarGroup>
              <SidebarGroupLabel>Administration</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>{administrationGroup.map((e) => renderMenuButton(e))}</SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </>
        )}
      </SidebarContent>
    </Sidebar>
  );
}
